import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Storage } from "@aws-amplify/storage";
import useDimensions from "react-use-dimensions";
import { Blurhash } from "react-blurhash";
import Ratio from "./Ratio";
import { recommendedPixels } from "../utils/imageQuality";
import { loadThumbnail, thumbnailPrintDpi } from "../utils/printThumbnail";
import { Camera as CameraIcon } from "styled-icons/fa-solid";
import { Spinner as SpinnerIcon } from "styled-icons/fa-solid";
import {
	postImageQualityWarning,
	postImageLoadingError,
	postPhotoDrop,
} from "../utils/postmessage";
import {
	PHOTO_DRAG_MIME,
	hasPhotoDragType,
	parsePhotoDragQuestionId,
	consumePostDragClick,
} from "../utils/photoDrag";
import { beginPhotoPointerDrag } from "../utils/photoPointerDrag";
import params from "../utils/params";
import { processImageWithFallback } from "../utils/rawImageProcessor";

const OuterContainer = styled.div`
  width: ${(props) => props.width || "100%"};
  ${(props) =>
		props.mb &&
		`
      margin-bottom: ${props.mb}vw;
    `}
`;

const InnerContainer = styled(Ratio)`
  width: 100%;
  ${(props) =>
		props.view !== "printPreview" &&
		props.view !== "print" &&
		!props.edited &&
		`background-color: #c4c4c4;
  `}
`;

const PlaceholderInnerContainer = styled.div`
  display: flex;
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  z-index: 10000;
`;

const StyledCameraIcon = styled(CameraIcon)`
  width: 10vw;
  color: rgba(0, 0, 0, 0.25);
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Img = styled.img`
  object-fit: cover;
  ${(props) => props.edited && `object-fit:contain;`}
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: ${(props) => (props.visible ? "block" : "none")};
`;

const BlurhashContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: ${(props) => (props.visible ? "block" : "none")};
`;

const WarningContainer = styled.div`
  z-index: ${(props) => props.zIndex};
  display: flex;
  position: absolute;
  right: 0;
  top: 0;
  width: 9vw;
  height: 9vw;
  justify-content: center;
  align-items: center;
`;

const UploadSpinnerContainer = styled.div`
  z-index: 10000;
  display: flex;
  position: absolute;
  left: 0;
  top: 0;
  width: 9vw;
  height: 9vw;
  justify-content: center;
  align-items: center;
`;

const UploadSpinnerIcon = styled(SpinnerIcon)`
  width: 5vw;
  color: #405577;
  animation: 2s linear ${spin} infinite;
`;

const Photo = ({
	imgObject,
	ratio,
	onClick,
	width,
	className,
	mb,
	loading,
	"data-editable-id": editableId,
	"data-editable-type": editableType,
}) => {
	const [ref, { width: recommendedWidth, height: recommendedHeight }] =
		useDimensions();
	const { view, identity } = params;

	const [imageUri, setImageUri] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [retry, setRetry] = useState(0);
	const [utifProcessedUri, setUtifProcessedUri] = useState(null);
	const [, setIsUtifProcessing] = useState(false);
	const [errorTimeoutId, setErrorTimeoutId] = useState(null);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [permanentError, setPermanentError] = useState(false);
	const [printThumbnail, setPrintThumbnail] = useState(null);
	const thumbnailAttempt = useRef(null);
	const currentImageKey = useRef(null);

	// Defensive: a missing `ratio` (e.g. an empty photo slot on a mis-created
	// page) must never crash the render. An uncaught throw here has no error
	// boundary above it, so it tears down the ENTIRE book and produces a blank
	// PDF. Fall back to a square so layout math stays finite.
	const [ratioWidth, ratioHeight] = (ratio || "1:1").split(":");

	let actualWidth = imgObject?.width;
	let actualHeight = imgObject?.height;

	//calculate the default cropped width and height
	if (!imgObject?.editedKey && ratioWidth > ratioHeight) {
		actualHeight = Math.round(imgObject?.width * (ratioHeight / ratioWidth));
	}

	if (!imgObject?.editedKey && ratioHeight > ratioWidth) {
		actualWidth = Math.round(imgObject?.height * (ratioWidth / ratioHeight));
	}

	if (!imgObject?.editedKey && ratioHeight === ratioWidth) {
		actualWidth = Math.min(imgObject?.width, imgObject?.height);
		actualHeight = Math.min(imgObject?.width, imgObject?.height);
	}

	const recommendedWidthPercentage = (
		Math.round((recommendedWidth / window.outerWidth) * 200) / 200
	).toFixed(4);
	const recommendedHeightPercentage = (
		Math.round((recommendedHeight / window.outerHeight) * 200) / 200
	).toFixed(4);
	// Slot and page are both measured in layout CSS px, so the threshold is
	// the same on iOS and Android (see utils/imageQuality.js).
	const { width: recommendedWidthPixels, height: recommendedHeightPixels } =
		recommendedPixels({
			slotWidth: recommendedWidth,
			pageWidth: document.documentElement.clientWidth,
			ratioWidth: Number(ratioWidth),
			ratioHeight: Number(ratioHeight),
		});
	const isImageQualityWarning =
		recommendedWidthPixels > actualWidth &&
		recommendedHeightPixels > actualHeight;

	// Key the fetch on the content that matters, not the imgObject identity:
	// re-posted pages hand us a deep copy of the same image, and re-signing
	// the URL then would make the browser re-download the photo.
	const imgKey =
		view === "preview" || view === "printPreview" || view === "review"
			? imgObject?.thumbnailKey
			: imgObject?.editedKey || imgObject?.originalKey;
	const hasBase64 = Boolean(imgObject?.base64);
	currentImageKey.current = imgKey;

	// biome-ignore lint/correctness/useExhaustiveDependencies: We want this to run on every
	useEffect(() => {
		const getImageUri = async () => {
			try {
				setIsLoading(true);
				setIsError(false);
				setImageLoaded(false); // Reset image loaded state for new image
				setPermanentError(false); // Reset permanent error state
				setUtifProcessedUri(null); // Reset UTIF state for new image
				setIsUtifProcessing(false);
				setPrintThumbnail(null);
				// Empty slot (no image yet): nothing to load, and imgKey is undefined.
				if (!imgKey) {
					setIsLoading(false);
					return;
				}
				const keyName = imgKey.split(".")[0];
				const keyEnding = imgKey.split(".")[1];
				const combined = keyName + "." + keyEnding;
				const imgUri = await Storage.get(combined, {
					level: "protected",
					identityId: process.env.REACT_APP_AWS_REGION + ":" + identity,
				});
				setImageUri(imgUri);
			} catch (e) {
				console.log(e.message);
				setIsError(true);
				setIsLoading(false);
			}
		};

		if (!hasBase64 && imgKey) {
			getImageUri();
		} else {
			setImageUri(null);
		}
	}, [retry, view, identity, imgKey, hasBase64]);

	useEffect(() => {
		if (imgObject?.base64) {
			setImageUri(null);
		}
	}, [imgObject]);

	// Cleanup effect for error timeout
	useEffect(() => {
		return () => {
			if (errorTimeoutId) {
				clearTimeout(errorTimeoutId);
			}
		};
	}, [errorTimeoutId]);

	const handleOnError = async (imageElement) => {
		const failedKey = imgKey;
		setIsLoading(true);
		setImageLoaded(false);

		if (retry === 1 && view === "preview") {
			postImageLoadingError(imgObject);
		}

		if (retry <= 2) {
			// Try fallback processing on each retry attempt
			if (imageUri && !utifProcessedUri) {
				try {
					setIsUtifProcessing(true);
					console.log(
						"Attempting fallback processing (UTIF/HEIC) for:",
						imageUri,
					);
					const processedUri = await processImageWithFallback(imageUri);
					setUtifProcessedUri(processedUri);
					setIsError(false);
					setIsLoading(false);
					setIsUtifProcessing(false);
					return;
				} catch (fallbackError) {
					console.log("Fallback processing failed:", fallbackError);
					setIsUtifProcessing(false);
				}
			}

			// Only replace a failed print asset. The thumbnail keeps the saved
			// crop; its decoded dimensions must provide at least 150 DPI here.
			if (view === "print" && imgObject?.thumbnailKey &&
				thumbnailAttempt.current !== failedKey) {
				thumbnailAttempt.current = failedKey;
				try {
					const uri = await Storage.get(imgObject.thumbnailKey, {
						level: "protected",
						identityId: process.env.REACT_APP_AWS_REGION + ":" + identity,
					});
					const dimensions = await loadThumbnail(uri);
					if (currentImageKey.current !== failedKey) return;
					const slot = imageElement.parentElement.getBoundingClientRect();
					const dpi = thumbnailPrintDpi({
						...dimensions, slotWidth: slot.width, slotHeight: slot.height,
						pageWidth: window.innerWidth,
						// Cover pages live in an iframe sized in physical CSS px.
						pageWidthMm: window.parent === window ? 305 : window.innerWidth / 96 * 25.4,
						fit: imgObject?.editedKey ? "contain" : "cover",
					});
					if (dpi >= 150) {
						setPrintThumbnail({ originalKey: failedKey, dpi });
						setUtifProcessedUri(uri);
						setIsError(false);
						return; // onLoad confirms this specific slot actually rendered.
					}
				} catch (error) {
					console.log("Print thumbnail unavailable:", error.message);
				}
			}

			setTimeout(() => {
				setRetry(retry + 1);
			}, 1000 * retry);
		} else {
			const timeoutId = setTimeout(() => {
				setIsError(true);
				setPermanentError(true);
				setIsLoading(false);
			}, 3000);
			setErrorTimeoutId(timeoutId);
		}
	};

	const handleOnClick = (event) => {
		// Some browsers fire a click on the drag source right after a drag;
		// swallow it so finishing a drag never opens the host's image picker.
		if (consumePostDragClick()) return;
		onClick(recommendedWidthPercentage, recommendedHeightPercentage);
	};

	// Drag-and-drop between photo slots (view=preview only): a COOPERATIVE
	// pointer drag, not native HTML5 DnD — Chromium does not route drag (or
	// captured pointer) events across this embed's process boundaries, so
	// the gesture is coordinated with the host via the message bridge. See
	// utils/photoPointerDrag.jsx. This handler only ARMS the drag; a plain
	// click (no movement past the threshold) stays a click.
	const handlePhotoPointerDown = (event) => {
		const img = event.currentTarget.querySelector
			? event.currentTarget.querySelector("img")
			: null;
		beginPhotoPointerDrag(editableId, img ? img.currentSrc : null, event);
	};

	const handleDragOver = (event) => {
		if (!hasPhotoDragType(event.dataTransfer)) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		event.currentTarget.classList.add("photo-drop-target");
	};

	const handleDragLeave = (event) => {
		event.currentTarget.classList.remove("photo-drop-target");
	};

	const handleDrop = (event) => {
		event.currentTarget.classList.remove("photo-drop-target");
		if (!hasPhotoDragType(event.dataTransfer)) return;
		event.preventDefault();
		const fromQuestionId = parsePhotoDragQuestionId(
			event.dataTransfer.getData(PHOTO_DRAG_MIME),
		);
		if (!fromQuestionId || fromQuestionId === editableId) return;
		postPhotoDrop(fromQuestionId, editableId);
	};

	const handleWarningClick = (event) => {
		event.stopPropagation();
		postImageQualityWarning(
			recommendedWidthPixels,
			recommendedHeightPixels,
			actualWidth,
			actualHeight,
		);
	};

	const handleUploadWarningClick = (event) => {
		event.stopPropagation();
		alert("There was an issue with the image. Please try to re-upload.");
	};

	const isUploadWarning = imgObject?.uploadWarning || false;
	const shouldHideBlurhash =
		view === "print" || view === "printPreview" || view === "review";
	const showBlurhash =
		imgObject?.blurhash &&
		!imageLoaded &&
		!permanentError &&
		!shouldHideBlurhash;
	const showImage =
		(imageUri || imgObject?.base64 || utifProcessedUri) && !permanentError;

	// Only view=preview gets drag affordances — like editable(), the
	// print/printPreview/review DOM stays byte-identical (no draggable
	// attribute, no drag handlers). Every slot accepts drops; only slots with
	// a rendered image are drag sources.
	const isDragEditable = view === "preview" && !!editableId;
	const dragProps = isDragEditable
		? {
				onDragOver: handleDragOver,
				onDragLeave: handleDragLeave,
				onDrop: handleDrop,
				...(showImage
					? {
							onPointerDown: handlePhotoPointerDown,
						}
					: {}),
			}
		: {};

	return (
		<OuterContainer
			ref={ref}
			key={imgObject?.thumbnailKey + retry}
			width={width}
			onClick={(event) => handleOnClick(event)}
			className={
				editableId && !imgObject
					? `${className || ""} editable-photo-empty`.trim()
					: className
			}
			data-editable-id={editableId}
			data-editable-type={editableType}
			data-print-image-state={view === "print" && imgKey
				? (imageLoaded ? "loaded" : permanentError || isError ? "error" : "pending") : undefined}
			data-print-image-key={view === "print" ? imgKey : undefined}
			mb={mb}
			{...dragProps}
		>
			<InnerContainer ratio={ratio} view={view} edited={imgObject?.editedKey}>
				{view !== "printPreview" && view !== "print" && (
					<PlaceholderInnerContainer>
						{((!isLoading && (!imgObject || permanentError)) ||
							(imgObject?.loading &&
								!imageUri &&
								!imgObject?.base64 &&
								!imgObject?.blurhash)) && <StyledCameraIcon />}
					</PlaceholderInnerContainer>
				)}
				{view === "preview" &&
					(imgObject?.uploading || imgObject?.loading || isLoading) && (
						<UploadSpinnerContainer>
							<UploadSpinnerIcon />
						</UploadSpinnerContainer>
					)}
				{showBlurhash && (
					<BlurhashContainer visible={true}>
						<Blurhash
							hash={imgObject.blurhash}
							width="100%"
							height="100%"
							resolutionX={32}
							resolutionY={32}
							punch={1}
						/>
					</BlurhashContainer>
				)}
				{showImage && (
					<>
						<Img
							data-print-thumbnail-for={imageLoaded ? printThumbnail?.originalKey : undefined}
							data-print-thumbnail-dpi={imageLoaded ? printThumbnail?.dpi : undefined}
							src={
								utifProcessedUri
									? utifProcessedUri
									: imgObject?.base64
										? `data:image/jpeg;base64, ${imgObject?.base64}`
										: imageUri
							}
							// Images are draggable by default, and the browser picks the
							// CLOSEST draggable element as the drag source — the img would
							// hijack the slot container's drag, adding Chrome's default
							// image payload ("Files", uri-list, the full-size ghost). That
							// spuriously summons the host's file-drop overlay, which then
							// swallows the whole photo drag. The container must be the
							// one and only drag source.
							draggable={false}
							visible={imageLoaded}
							edited={imgObject?.editedKey}
							onLoad={() => {
								// Clear any pending error timeout if the image loads successfully
								if (errorTimeoutId) {
									clearTimeout(errorTimeoutId);
									setErrorTimeoutId(null);
								}
								setIsLoading(false);
								setIsError(false);
								setImageLoaded(true);
								setPermanentError(false);
							}}
							onError={(event) => handleOnError(event.currentTarget)}
							width={imgObject?.width}
							height={imgObject?.height}
							ratioWidth={ratioWidth}
							ratioHeight={ratioHeight}
						/>
						{(view === "preview" || view === "printPreview") &&
							(isUploadWarning ||
								(isError && imgObject?.thumbnailRegenerated)) && (
								<WarningContainer
									onClick={(event) => handleUploadWarningClick(event)}
									zIndex={11000}
								>
									<svg
										width="9vw"
										height="9vw"
										viewBox="0 0 32 32"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M12.1029 7.75C13.8349 4.75 18.1651 4.75 19.8971 7.75L25.0933 16.75C26.8253 19.75 24.6603 23.5 21.1962 23.5H10.8038C7.33974 23.5 5.17468 19.75 6.90673 16.75L12.1029 7.75Z"
											fill="#f2545b"
											stroke="white"
										/>
										<path
											d="M16.9783 10.0928C16.9783 10.2744 16.9725 10.4385 16.9608 10.585C16.949 10.7314 16.9256 10.9629 16.8904 11.2793L16.2049 17.0361H15.4139L14.7283 11.2793C14.6697 10.7637 14.6404 10.3682 14.6404 10.0928C14.6404 9.64746 14.7576 9.31934 14.992 9.1084C15.2264 8.89746 15.493 8.79199 15.7918 8.79199C16.1317 8.79199 16.4129 8.90039 16.6356 9.11719C16.8641 9.32812 16.9783 9.65332 16.9783 10.0928ZM17.1893 19.9189C17.1893 20.3057 17.0545 20.6309 16.785 20.8945C16.5154 21.1582 16.1903 21.29 15.8094 21.29C15.4344 21.29 15.1121 21.1582 14.8426 20.8945C14.5731 20.6309 14.4383 20.3057 14.4383 19.9189C14.4383 19.5322 14.5701 19.2041 14.8338 18.9346C15.0975 18.665 15.4227 18.5303 15.8094 18.5303C16.1961 18.5303 16.5213 18.665 16.785 18.9346C17.0545 19.1982 17.1893 19.5264 17.1893 19.9189Z"
											fill="white"
										/>
									</svg>
								</WarningContainer>
							)}
						{view === "preview" && isImageQualityWarning && (
							<WarningContainer
								onClick={(event) => handleWarningClick(event)}
								zIndex={10000}
							>
								<svg
									width="9vw"
									height="9vw"
									viewBox="0 0 32 32"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M12.1029 7.75C13.8349 4.75 18.1651 4.75 19.8971 7.75L25.0933 16.75C26.8253 19.75 24.6603 23.5 21.1962 23.5H10.8038C7.33974 23.5 5.17468 19.75 6.90673 16.75L12.1029 7.75Z"
										fill="#D6A969"
										stroke="white"
									/>
									<path
										d="M16.9783 10.0928C16.9783 10.2744 16.9725 10.4385 16.9608 10.585C16.949 10.7314 16.9256 10.9629 16.8904 11.2793L16.2049 17.0361H15.4139L14.7283 11.2793C14.6697 10.7637 14.6404 10.3682 14.6404 10.0928C14.6404 9.64746 14.7576 9.31934 14.992 9.1084C15.2264 8.89746 15.493 8.79199 15.7918 8.79199C16.1317 8.79199 16.4129 8.90039 16.6356 9.11719C16.8641 9.32812 16.9783 9.65332 16.9783 10.0928ZM17.1893 19.9189C17.1893 20.3057 17.0545 20.6309 16.785 20.8945C16.5154 21.1582 16.1903 21.29 15.8094 21.29C15.4344 21.29 15.1121 21.1582 14.8426 20.8945C14.5731 20.6309 14.4383 20.3057 14.4383 19.9189C14.4383 19.5322 14.5701 19.2041 14.8338 18.9346C15.0975 18.665 15.4227 18.5303 15.8094 18.5303C16.1961 18.5303 16.5213 18.665 16.785 18.9346C17.0545 19.1982 17.1893 19.5264 17.1893 19.9189Z"
										fill="white"
									/>
								</svg>
							</WarningContainer>
						)}
					</>
				)}
			</InnerContainer>
		</OuterContainer>
	);
};
export default Photo;
